"use client";
import { logger } from "@/utils/logger";

import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Button from "@/components/ui/Button";
import { Search, Settings, MessageSquare, Clock, HeartHandshake, Phone, TrendingUp, ShieldCheck, Zap } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { EmptyState } from "@/components/ui/EmptyState";
import Link from "next/link";
import Image from "next/image";
import { useSettings } from "@/context/SettingsContext";

const approachSteps = [
  {
    id: 1,
    title: "Understanding",
    icon: Search,
    color: "text-blue-600",
    bg: "bg-blue-100",
    border: "border-blue-200",
    desc: "We start by getting to grips with understanding your business and how it works. Understanding where you want to drive things – are you looking for cost reduction, improved productivity, additional features, waste elimination – probably all of the above!\n\nThere then follows a detailed examination of your existing infrastructure; the number of machines / users that you are looking to support, quantifying the print and mail volumes that you are dealing with and testing our solutions against those expectations and operating conditions."
  },
  {
    id: 2,
    title: "Constructing a Solution",
    icon: Settings,
    color: "text-teal-600",
    bg: "bg-teal-100",
    border: "border-teal-200",
    desc: "Because we are not tied to any one manufacturer we are at liberty to use our extensive market and product knowledge to gather a best feature / value proposition from the whole market; using our integration knowledge to put together a solution which will answer your business requirements and work out of the box with minimum downtime and thorough product training."
  },
  {
    id: 3,
    title: "Presenting the Solution",
    icon: MessageSquare,
    color: "text-purple-600",
    bg: "bg-purple-100",
    border: "border-purple-200",
    desc: "Our proposal to you will clearly show how your business requirements will be met. We will also present options and add-ons that would enhance the solution in the future for planned business growth and demonstrate how the current solution can be adapted to meet your future needs."
  },
  {
    id: 4,
    title: "Delivering first time",
    icon: Clock,
    color: "text-amber-600",
    bg: "bg-amber-100",
    border: "border-amber-200",
    desc: "We will organise and co-ordinate the implementation of the solution in close partnership with yourselves. We will use our long experience to ensure that clear and accurate communication with manufacturers and suppliers results in a closely co-ordinated delivery and commissioning process. On successful commissioning we will invite you to sign –off the delivery and implementation phase by reviewing the original proposal with you against the physical reality."
  },
  {
    id: 5,
    title: "Maintaining relationship",
    icon: HeartHandshake,
    color: "text-rose-600",
    bg: "bg-rose-100",
    border: "border-rose-200",
    desc: "We regard delivery as just the start of our relationship. Regular account reviews will enable us to keep in touch with your developing business needs and help you to get the best out of your office equipment."
  }
];

import DOMPurify from 'isomorphic-dompurify';

export default function OurApproach() {
  const settings = useSettings();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeStepId, setActiveStepId] = useState<number | string | null>(null);
  const [approachContent, setApproachContent] = useState<any>(null);
  const [dynamicSteps, setDynamicSteps] = useState<any[]>([]);
  const [capabilities, setCapabilities] = useState<any[]>([]);

  const orbRef = useRef<HTMLDivElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const centralNodeRef = useRef<HTMLDivElement>(null);
  const detailPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, approachRes, capRes] = await Promise.all([
          fetch('/api/admin/page-content?pageId=approach'),
          fetch('/api/public/approach'),
          fetch('/api/public/capabilities')
        ]);
        const contentJson = await contentRes.json();
        const approachJson = await approachRes.json();
        const capJson = await capRes.json();

        if (capJson.success && capJson.data) {
          setCapabilities(capJson.data);
        }

        if (contentJson.success && contentJson.data) {
          setApproachContent(contentJson.data);
        }

        if (approachJson.success && approachJson.data && approachJson.data.length > 0) {
          const mappedSteps = approachJson.data.map((s: any, idx: number) => {
            const fallback = approachSteps[idx % approachSteps.length];
            const DynamicIcon = s.icon ? (LucideIcons as any)[s.icon] : null;
            return {
              id: s._id,
              title: s.title,
              desc: s.description,
              icon: DynamicIcon || fallback.icon,
              customImage: s.customImage || null,
              color: s.color || fallback.color,
              bg: fallback.bg,
              border: fallback.border,
              order: s.order,
            };
          });
          setDynamicSteps(mappedSteps);
          setActiveStepId(mappedSteps[0].id);
        } else if (contentJson.success && contentJson.data && contentJson.data.steps && contentJson.data.steps.length > 0) {
          // Fallback to old page-content steps if DB is empty
          const mappedSteps = contentJson.data.steps.map((s: any, idx: number) => ({
            id: s.id,
            title: s.title,
            desc: s.desc,
            icon: approachSteps[idx % approachSteps.length].icon,
            color: approachSteps[idx % approachSteps.length].color,
            bg: approachSteps[idx % approachSteps.length].bg,
            border: approachSteps[idx % approachSteps.length].border,
            order: idx + 1,
          }));
          setDynamicSteps(mappedSteps);
          setActiveStepId(mappedSteps[0].id);
        }
      } catch (err) {
        logger.error("Failed to fetch approach data", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isLoading) return;

    gsap.registerPlugin(ScrollTrigger);

    if (orbRef.current) gsap.to(orbRef.current, { y: -20, x: 10, scale: 1.05, duration: 7.5, yoyo: true, repeat: -1, ease: "sine.inOut" });
    if (heroContentRef.current) gsap.fromTo(heroContentRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" });
    if (centralNodeRef.current) gsap.to(centralNodeRef.current, { boxShadow: "0px 0px 40px 15px rgba(13,148,136,0.1)", duration: 1.5, yoyo: true, repeat: -1, ease: "sine.inOut" });

    // Methodology scroll animations
    const ctx = gsap.context(() => {
      gsap.utils.toArray(".methodology-card").forEach((el: any) => {
        gsap.fromTo(el, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.6, delay: 0.3, scrollTrigger: { trigger: el, once: true, start: "top 85%" } });
      });
      gsap.utils.toArray(".methodology-header").forEach((el: any) => {
        gsap.fromTo(el, { opacity: 0, x: 20 }, { opacity: 1, x: 0, duration: 0.6, scrollTrigger: { trigger: el, once: true, start: "top 85%" } });
      });
      gsap.utils.toArray(".methodology-feature").forEach((el: any) => {
        const delay = parseFloat(el.dataset.idx || "0") * 0.15;
        gsap.fromTo(el, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, delay, scrollTrigger: { trigger: el, once: true, start: "top 85%" } });
      });
    });

    return () => ctx.revert();
  }, [isLoading]);

  useEffect(() => {
    if (detailPanelRef.current) {
      gsap.fromTo(detailPanelRef.current,
        { opacity: 0, y: 20, filter: "blur(4px)" },
        { opacity: 1, y: 0, filter: "blur(0px)", duration: 0.4, ease: "power2.out", overwrite: "auto" }
      );
    }
  }, [activeStepId, dynamicSteps]);

  const activeStep = dynamicSteps.find(s => s.id === activeStepId) || dynamicSteps[0];

  return (
    <div className="min-h-screen font-sans overflow-x-hidden bg-white">

      {/* 1. HERO SECTION */}
      <section className="relative pt-44 md:pt-52 pb-16 px-6 sm:px-8 w-full flex flex-col items-center text-center overflow-hidden min-h-[450px] justify-center bg-white">
        {/* Professional Left-to-Right Curved Background */}
        <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
          {/* Base gradient ensuring left side is purely white for the logo */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white to-[#ccfbf1]/30" />

          {/* Elegant sweeping SVG curves on the right side */}
          <svg className="absolute top-0 right-0 h-full w-[100%] md:w-[70%] opacity-80" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M100,0 L100,100 L0,100 C30,70 40,30 0,0 Z" fill="#f0fdfa" />
          </svg>

          <svg className="absolute top-0 right-0 h-full w-[80%] md:w-[50%] opacity-50" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M100,0 L100,100 L0,100 C40,80 50,20 0,0 Z" fill="#ccfbf1" />
          </svg>

          {/* Subtle animated floating orb purely on the right side */}
          <div
            ref={orbRef}
            className="absolute top-0 -right-20 w-[300px] sm:w-[400px] md:w-[600px] h-[300px] sm:h-[400px] md:h-[600px] bg-gradient-to-bl from-[#99f6e4]/20 to-transparent rounded-full blur-3xl"
          />
        </div>

        <div
          ref={heroContentRef}
          className="max-w-4xl relative z-10 opacity-0"
        >
          <span className="inline-block px-4 py-1.5 bg-white border border-[#99f6e4] text-[#0f766e] text-[10px] sm:text-[11px] font-sans font-bold uppercase tracking-[0.2em] mb-8 rounded-full shadow-sm">
            Proven Methodology
          </span>
          <h1 className="text-[40px] md:text-[56px] font-serif font-extrabold tracking-tight text-gray-900 leading-tight mb-6">
            How We Work
          </h1>
          <p className="font-sans text-gray-500 text-[14px] md:text-[15px] max-w-2xl mx-auto leading-relaxed font-medium">
            Our methodology is designed to provide 100% independent and transparent advice.
          </p>
        </div>
      </section>

      {/* 2. ORBITAL GRAPH & DETAILS (Full Width Background) */}
      <section className="relative w-full bg-gradient-to-r from-[#f0fdfa] via-white to-[#ccfbf1]/30 py-24 border-t border-[#ccfbf1]/50 overflow-hidden">
        {isLoading ? (
          <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col lg:flex-row items-center justify-center gap-16 lg:gap-24 relative z-10">
            {/* Left: Skeleton Approach Chart */}
            <div className="relative w-[180px] h-[180px] sm:w-[400px] sm:h-[400px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] flex items-center justify-center shrink-0 mt-8 mb-16 lg:mt-0 lg:mb-0">
              <div className="absolute w-[100%] h-[100%] border border-[#99f6e4]/40 rounded-full border-dashed" />
              <div className="absolute w-[60%] h-[60%] border border-[#99f6e4]/30 rounded-full" />

              <div className="absolute z-20 w-20 h-20 sm:w-32 sm:h-32 md:w-40 md:h-40 bg-white rounded-full flex flex-col items-center justify-center shadow-xl border border-[#99f6e4]/60">
                <div className="w-8 md:w-12 h-2.5 md:h-3 bg-teal-100 rounded animate-pulse mb-1 md:mb-2"></div>
                <div className="w-14 md:w-20 h-4 md:h-6 bg-teal-100 rounded animate-pulse"></div>
              </div>

              <div className="absolute w-full h-full">
                {[1, 2, 3, 4, 5].map((_, idx) => {
                  const angle = (idx / 5) * (2 * Math.PI) - (Math.PI / 2);
                  const radius = 50;
                  const left = `calc(50% + ${Math.cos(angle) * radius}%)`;
                  const top = `calc(50% + ${Math.sin(angle) * radius}%)`;

                  return (
                    <div key={idx} className="absolute z-30" style={{ left, top, transform: "translate(-50%, -50%)" }}>
                      <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2 px-2 py-2 sm:px-5 sm:py-3 bg-white rounded-xl sm:rounded-full shadow-md border border-gray-100">
                        <div className="w-3.5 h-3.5 md:w-4 md:h-4 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="w-10 sm:w-16 h-2 sm:h-2.5 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Skeleton Detail Panel */}
            <div className="w-full max-w-lg relative min-h-[350px] flex flex-col justify-center">
              <div className="bg-white/90 backdrop-blur-xl border border-white p-8 md:p-10 rounded-3xl shadow-[0_20px_40px_rgba(13,148,136,0.08)] relative overflow-hidden">
                <div className="relative z-10 w-12 h-12 rounded-full mb-6 bg-gray-100/50 animate-pulse border border-gray-100 shadow-sm"></div>
                <div className="relative z-10 w-16 h-3 bg-teal-100/60 rounded animate-pulse mb-3"></div>
                <div className="relative z-10 w-48 h-8 bg-gray-200/60 rounded animate-pulse mb-6"></div>
                <div className="relative z-10 space-y-4">
                  <div className="w-full h-3 md:h-4 bg-gray-100/80 rounded animate-pulse"></div>
                  <div className="w-full h-3 md:h-4 bg-gray-100/80 rounded animate-pulse"></div>
                  <div className="w-5/6 h-3 md:h-4 bg-gray-100/80 rounded animate-pulse"></div>
                  <div className="w-4/5 h-3 md:h-4 bg-gray-100/80 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        ) : dynamicSteps.length === 0 ? (
          <div className="max-w-3xl mx-auto px-6 py-12">
            <EmptyState
              icon={<Settings className="w-8 h-8 text-teal-600 opacity-80" strokeWidth={1.5} />}
              title="Our Approach Coming Soon"
              message="We are currently updating our strategic approach stages. Please check back later."
            />
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col lg:flex-row items-center justify-center gap-16 lg:gap-24 relative z-10">

            {/* Left: Interactive Approach Chart */}
            <div className="relative w-[180px] h-[180px] sm:w-[400px] sm:h-[400px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] flex items-center justify-center shrink-0 mt-8 mb-16 lg:mt-0 lg:mb-0">

              {/* Background Rings */}
              <div className="absolute w-[100%] h-[100%] border border-[#99f6e4]/40 rounded-full border-dashed" />
              <div className="absolute w-[60%] h-[60%] border border-[#99f6e4]/30 rounded-full" />

              {/* Central Node */}
              <div
                ref={centralNodeRef}
                className="absolute z-20 w-20 h-20 sm:w-32 sm:h-32 md:w-40 md:h-40 bg-white rounded-full flex flex-col items-center justify-center border border-[#99f6e4]/60"
              >
                <span className="text-[9px] md:text-[12px] font-bold text-[#0f766e] uppercase tracking-widest mb-1">Core</span>
                <span className="text-[14px] md:text-[22px] font-bold text-gray-900 font-serif text-center leading-tight">Our<br />Approach</span>

                {/* Click Hint */}
                <div className="absolute -bottom-4 md:-bottom-5 bg-teal-50 border border-teal-100 text-teal-700 text-[8px] md:text-[10px] font-bold uppercase tracking-widest px-3 py-1 md:px-4 md:py-1.5 rounded-full shadow-sm whitespace-nowrap">
                  Click stages
                </div>
              </div>

              {/* Fixed Nodes (No rotation to guarantee readability) */}
              <div className="absolute w-full h-full">
                {dynamicSteps.map((step, idx) => {
                  const total = 5; // Always use 5 slots for consistent design
                  const positionIndex = (step.order || idx + 1) - 1;
                  // Start from top (offset by -90 deg / -PI/2)
                  const angle = (positionIndex / total) * (2 * Math.PI) - (Math.PI / 2);
                  const radius = 50; // Place them exactly on the 100% border

                  const left = `calc(50% + ${Math.cos(angle) * radius}%)`;
                  const top = `calc(50% + ${Math.sin(angle) * radius}%)`;

                  return (
                    <div
                      key={step.id}
                      className="absolute z-30"
                      style={{ left, top, transform: "translate(-50%, -50%)" }}
                    >
                      <button
                        onClick={() => setActiveStepId(step.id)}
                        className={`relative flex items-center justify-center text-center flex-col sm:flex-row gap-1 sm:gap-2.5 px-2 py-1.5 sm:px-4 sm:py-2.5 rounded-xl sm:rounded-full font-bold text-[7.5px] sm:text-[10px] md:text-[11px] uppercase tracking-wider transition-all duration-300 shadow-md border ${activeStepId === step.id
                          ? `bg-white ${step.color?.startsWith('#') ? '' : step.color} border-current scale-110 z-40 shadow-lg shadow-teal-900/10`
                          : "bg-white text-gray-700 border-gray-200 hover:text-[#0d9488] hover:border-[#99f6e4] hover:scale-105 z-30 hover:shadow-lg"
                          }`}
                        style={{ color: activeStepId === step.id && step.color?.startsWith('#') ? step.color : undefined }}
                      >
                        {step.customImage ? (
                          <div className="w-4 h-4 md:w-5 md:h-5 relative shrink-0">
                            <Image src={step.customImage} alt={step.title} fill sizes="20px" className="object-cover rounded-full" />
                          </div>
                        ) : (
                          <step.icon className={`w-3.5 h-3.5 md:w-4 md:h-4 shrink-0 ${activeStepId === step.id ? (step.color?.startsWith('#') ? '' : step.color) : "text-gray-400"}`} style={{ color: activeStepId === step.id && step.color?.startsWith('#') ? step.color : undefined }} />
                        )}
                        <span className="whitespace-normal sm:whitespace-nowrap leading-[1.15] max-w-[65px] sm:max-w-none text-center">{step.title}</span>

                        {/* Active indicator ring */}
                        {activeStepId === step.id && (
                          <div
                            className={`absolute -inset-1.5 rounded-full border border-current opacity-30 animate-pulse ${step.color?.startsWith('#') ? '' : step.color}`}
                            style={{ borderColor: step.color?.startsWith('#') ? step.color : undefined }}
                          />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Detail Panel */}
            <div className="w-full max-w-lg relative min-h-[350px] flex flex-col justify-center">
              <div
                ref={detailPanelRef}
                className="bg-white/90 backdrop-blur-xl border border-white p-8 md:p-10 rounded-3xl shadow-[0_20px_40px_rgba(13,148,136,0.08)] relative overflow-hidden opacity-0"
              >
                {/* Decorative background curve inside the card */}
                <div className={`absolute -right-20 -top-20 w-40 h-40 rounded-full blur-3xl opacity-30 ${activeStep?.color?.startsWith('#') ? '' : activeStep?.bg}`} style={{ backgroundColor: activeStep?.color?.startsWith('#') ? activeStep?.color : undefined }} />

                <div
                  className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center mb-6 ${activeStep?.color?.startsWith('#') ? '' : activeStep?.bg} ${activeStep?.color?.startsWith('#') ? '' : activeStep?.border} border shadow-sm`}
                  style={activeStep?.color?.startsWith('#') ? {
                    backgroundColor: `color-mix(in srgb, ${activeStep?.color} 15%, transparent)`,
                    borderColor: `color-mix(in srgb, ${activeStep?.color} 30%, transparent)`
                  } : {}}
                >
                  {activeStep?.customImage ? (
                    <div className="w-6 h-6 relative shrink-0">
                      <Image src={activeStep?.customImage || ''} alt={activeStep?.title || 'Step'} fill sizes="24px" className="object-cover rounded-full" />
                    </div>
                  ) : (
                    activeStep?.icon && <activeStep.icon className={`w-5 h-5 ${activeStep?.color?.startsWith('#') ? '' : activeStep?.color}`} style={{ color: activeStep?.color?.startsWith('#') ? activeStep?.color : undefined }} />
                  )}
                </div>
                <div className="relative z-10 flex items-center gap-3 mb-2">
                  <span className="text-[11px] font-bold tracking-widest uppercase text-teal-600/60">
                    Stage {activeStep?.order || (dynamicSteps.findIndex(s => s.id === activeStep?.id) + 1)}
                  </span>
                </div>
                <h2 className="relative z-10 text-[26px] md:text-[32px] font-serif font-bold text-gray-900 mb-6 leading-tight">
                  {activeStep?.title}
                </h2>
                <div className="relative z-10 space-y-4">
                  {activeStep?.desc.split('\n\n').map((paragraph: string, i: number) => (
                    <p key={i} className="text-[14px] md:text-[15px] text-gray-600 leading-relaxed font-sans" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(paragraph) }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* 3. CTA SECTION */}
      <section className="relative py-24 px-6 sm:px-8 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-1 bg-[#0d9488] mx-auto mb-10" />
          <p className="font-serif text-[22px] md:text-[28px] text-[#334155] leading-relaxed mb-12 max-w-3xl mx-auto">
            "We believe that a combination of our complete independence, product knowledge and long experience represents a unique opportunity for you to benefit from our unbiased approach."
          </p>
          <p className="text-[14px] md:text-[15px] text-gray-500 max-w-2xl mx-auto leading-relaxed mb-10">
            Contact us now with your next project requirements and see how we can help, also see how our approach differs to those you have used in the past. <strong className="text-gray-900 font-bold">The costs of our consultancy services are totally free of charge to you.</strong>
          </p>

          {capabilities.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-10 max-w-[800px] mx-auto">
              {capabilities.map((cap, idx) => {
                const stripHtml = (html: any) => (html || '').replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, '').trim();
                const sDesc = stripHtml(cap.shortDescription);
                const lDesc = stripHtml(cap.longDescription);
                const hasDetails = (sDesc.length > 0 && sDesc.toLowerCase() !== 'n/a') || (lDesc.length > 0 && lDesc.toLowerCase() !== 'n/a');

                return hasDetails ? (
                  <Link
                    key={idx}
                    href={`/capabilities/${cap.slug}`}
                    className="inline-block bg-gray-50 border border-gray-100 text-gray-600 hover:text-[#0d9488] hover:bg-[#f0fdfa] hover:border-[#99f6e4] font-sans font-bold text-[11px] md:text-[12px] px-5 py-2.5 rounded-full transition-all cursor-pointer shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-md hover:-translate-y-0.5"
                  >
                    {cap.title}
                  </Link>
                ) : (
                  <span
                    key={idx}
                    className="inline-block bg-gray-50 border border-gray-100 text-gray-600 font-sans font-bold text-[11px] md:text-[12px] px-5 py-2.5 rounded-full transition-all cursor-default shadow-[0_2px_10px_rgba(0,0,0,0.02)]"
                  >
                    {cap.title}
                  </span>
                );
              })}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button href="/contact" variant="solid" className="py-4 px-8 bg-[#0a0a0a] hover:!bg-[#0d9488] hover:!border-[#0d9488] text-white rounded-full font-bold shadow-lg w-full sm:w-auto uppercase tracking-widest text-[11px] sm:text-[12px] flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5">
              Get in Touch Today
            </Button>
            {settings.contactPhone?.trim() && (
              <a href={`tel:${settings.contactPhone}`} className="flex items-center gap-2 text-[13px] sm:text-[14px] font-medium text-gray-600 bg-[#f8fafc] hover:bg-[#f1f5f9] transition-colors px-6 py-4 rounded-full border border-gray-100 w-full sm:w-auto justify-center cursor-pointer">
                <Phone className="w-4 h-4 text-[#0d9488]" />
                <span>{settings.contactPhone}</span>
              </a>
            )}
          </div>

          <p className="text-[12px] md:text-[13px] text-gray-500 mt-10">
            To find out more or for a free, no obligation consultation please {settings.contactPhone?.trim() ? "call the number above or email us at" : "email us at"} <a href={`mailto:${settings.contactEmail}`} className="text-[#0f766e] hover:underline font-medium">{settings.contactEmail}</a>
          </p>
        </div>
      </section>

      {/* 4. NEW CREATIVE METHODOLOGY SECTION (Moved to Bottom) */}
      <section className="relative py-24 px-6 sm:px-8 max-w-7xl mx-auto bg-white border-t border-gray-100 z-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Images/Visuals Side */}
          <div className="relative h-[450px] sm:h-[550px] w-full rounded-3xl overflow-hidden group shadow-sm">
            <div className="absolute inset-0 bg-gray-900/10 group-hover:bg-transparent transition-colors duration-500 z-10" />
            <img
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=1000"
              alt="Consultancy Meeting"
              className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
            />
            {/* Floating Card Animation */}
            <div
              className="methodology-card opacity-0 absolute bottom-6 left-6 right-6 sm:bottom-10 sm:left-10 sm:right-10 bg-white/95 backdrop-blur-xl p-6 sm:p-8 rounded-2xl shadow-2xl z-20"
            >
              <div className="flex items-center gap-4 sm:gap-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#ccfbf1]/50 text-[#0d9488] rounded-full flex items-center justify-center shrink-0 border border-[#99f6e4]/50">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-serif font-bold text-gray-900 text-[18px] sm:text-[20px]">Data-Driven Optimisation</h3>
                  <p className="text-[13px] sm:text-[14px] text-gray-500 font-sans mt-1">Leveraging pure market metrics for objective results.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Text/Icons Side */}
          <div className="space-y-12">
            <div
              className="methodology-header opacity-0"
            >
              <span className="text-[11px] font-bold tracking-widest uppercase text-[#0d9488] mb-4 block">The CompareAll Standard</span>
              <h2 className="font-serif text-[36px] sm:text-[44px] font-bold tracking-tight text-gray-900 leading-[1.1]">
                Uncompromising integrity in every decision.
              </h2>
            </div>

            <div className="space-y-8">
              {[
                { icon: ShieldCheck, title: "100% Impartial Sourcing", desc: "We operate with zero manufacturer ties, meaning our recommendations are governed purely by what serves your business best." },
                { icon: Zap, title: "Frictionless Integration", desc: "Our deep product knowledge allows us to design solutions that integrate seamlessly into your existing workflow with zero downtime." },
                { icon: HeartHandshake, title: "Long-Term Partnership", desc: "Delivery is just the beginning. We provide continuous account reviews to ensure your infrastructure evolves alongside your business." }
              ].map((feature, idx) => (
                <div
                  key={idx}
                  data-idx={idx}
                  className="methodology-feature opacity-0 flex gap-5 sm:gap-6 group"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-50 group-hover:bg-[#f0fdfa] border border-gray-100 group-hover:border-[#ccfbf1] rounded-2xl flex items-center justify-center shrink-0 transition-colors duration-300">
                    <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#0d9488]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-[18px] sm:text-[20px] font-bold text-gray-900 mb-2 group-hover:text-[#0d9488] transition-colors">{feature.title}</h3>
                    <p className="font-sans text-[14px] sm:text-[15px] text-gray-600 leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

    </div>
  );
}
